import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ButtonComponent } from '../button/button.component';
import { CounterComponent } from '../counter/counter.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { AddLocalePipe } from '../../pipes/url-locale.pipe';
import { CartService } from '../../pages/cart-page/cart.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateRoutePipe } from '../../pipes/translate-route.pipe';
import { FormsModule } from '@angular/forms';
import { isSKU } from '../../helpers/regex-and-matching';
import { SpreeService } from '../../services/spree-client/storefront/spree.service';
import { filter, map } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { TopBarComponent } from '../top-bar/top-bar.component';
import { WishlistService } from '../../services/wishlist.service';
import { StateService } from '../../services/state.service';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-header',
  imports: [
    CounterComponent,
    SvgIconComponent,
    ButtonComponent,
    RouterLink,
    TranslocoModule,
    AddLocalePipe,
    TranslateRoutePipe,
    FormsModule,
    TopBarComponent,
    MegamenuComponent,
    AsyncPipe,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly client = inject(SpreeService);
  private readonly cart = inject(CartService);
  private readonly router = inject(Router);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly addLocale = inject(AddLocalePipe);
  private readonly translateRoute = inject(TranslateRoutePipe);
  private readonly wishlistService = inject(WishlistService);
  private readonly authenticationService = inject(AuthenticationService);
  readonly stateService = inject(StateService);

  locale = this.stateService.activeLocaleS;
  cartItemCount = toSignal(this.cart.itemCount$);
  wishlistCount = computed(() => this.wishlistService.items().length);

  ngContentZIndex = signal(2);

  isMobileSearchOpen = false;

  get isLoggedIn(): boolean {
    return this.authenticationService.isLoggedIn();
  }

  @ViewChild(MegamenuComponent)
  menu!: MegamenuComponent;

  @ViewChild('mobileSearchInput')
  mobileSearchInput?: ElementRef<HTMLInputElement>;

  @HostBinding('class.megamenu-opened')
  isMegamenuOpened = false;

  searchInputValue = '';

  ngOnInit() {
    this.activeRoute.url
      .pipe(
        filter(([firstSegment]) => firstSegment?.path === 'search'),
        map(() => this.activeRoute.snapshot.queryParams['q']),
      )
      .subscribe((query) => (this.searchInputValue = query));
  }

  onMenuOpenChange(isOpened: boolean): void {
    this.ngContentZIndex.set(isOpened ? -2 : 20);
    this.isMegamenuOpened = isOpened;
    this.cdr.markForCheck();
  }

  onHamburger(): void {
    this.menu.openMobile();
  }

  onSearchInputClick(e: Event): void {
    (e.target as HTMLElement).focus();
  }

  onMobileSearchToggle(): void {
    this.isMobileSearchOpen = !this.isMobileSearchOpen;
    setTimeout(() => this.mobileSearchInput?.nativeElement.focus());
  }

  onSearch(): void {
    if (!isSKU(this.searchInputValue)) {
      return void this.router.navigate(
        [this.addLocale.transform(this.translateRoute.transform('/search'))],
        {
          queryParams: { q: this.searchInputValue },
        },
      );
    }

    this.client.products
      .list({
        filter: {
          skus: this.searchInputValue,
        },
      })
      .subscribe((response) => {
        const slug = response.data[0]?.attributes.slug;

        if (slug) {
          void this.router.navigate([this.addLocale.transform('/p'), slug]);
        }
      });
  }
}
